/**
 * execution-stats-text — TODO texto que a listagem de workflows escreve sobre
 * execução (spec 2026-09-20 §4.2, D19).
 *
 * Puro, sem React: o front não tem testing-library, então texto se prova em
 * vitest (ambiente `node`) e componente se prova no Playwright.
 *
 * Duas regras que vieram de incidente:
 *  - o período (30 dias) é o que veio na RESPOSTA, nunca escrito à mão aqui
 *    (CLAUDE.md NUNCA #86 — rótulo com período fixo já mentiu em produção);
 *  - o tempo relativo é o do `date-fns` em pt-BR, sem retoque. Ele troca de
 *    unidade aos 30 dias: uma execução de 45 dias atrás sai como
 *    "há cerca de 2 meses", nunca "há 45 dias".
 */
import { formatDistance } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import type {
  WorkflowExecutionStats,
  WorkflowExecutionStatsBucket,
  WorkflowExecutionStatus,
} from '@troia-v3/types';

/**
 * Rótulo do status da ÚLTIMA execução no card e na linha. Feminino/verbo, para
 * concordar com "execução" — mapa PRÓPRIO da listagem: a barra lateral do
 * histórico tem o dela (masculino) e não é tocada.
 */
export const LAST_EXECUTION_STATUS_LABELS: Record<WorkflowExecutionStatus, string> = {
  completed: '✓ Concluída',
  failed: 'Falhou',
  cancelled: 'Cancelada',
  running: 'Em andamento',
  pending: 'Na fila',
  suspended: 'Aguardando',
};

/**
 * Rótulo da última execução quando o servidor manda o BALDE — a mesma régua que
 * conta as falhas embaixo. Sem isto o card se contradiz: uma execução gravada
 * `failed` com a janela de 24h do cliente fechada sai do balde `customerFailed`
 * (não conta como falha nossa) e a linha de cima dizia "Falhou" ao lado de
 * "0 falhas". Só estes três baldes mudam o texto; nos outros vale o status cru,
 * que distingue "Na fila" de "Aguardando" — distinção que o balde `running` perde.
 *
 * "Falha do cliente" e "Interrompida" são os MESMOS rótulos dos selos do
 * histórico (Fase 1).
 */
export const LAST_EXECUTION_BUCKET_LABELS: Partial<Record<WorkflowExecutionStatsBucket, string>> = {
  customerFailed: 'Falha do cliente',
  interrupted: 'Interrompida',
  failed: 'Falhou',
};

/** Workflow que nunca rodou de verdade (teste não conta — D19). */
export const NEVER_EXECUTED_LABEL = 'Nunca executou';

/** "1 execução" / "128 execuções" — ternário no lugar, convenção do módulo. */
export function formatExecutionCount(total: number): string {
  return `${total} ${total === 1 ? 'execução' : 'execuções'}`;
}

/** "0 falhas" / "1 falha" / "3 falhas". */
export function formatFailureCount(failed: number): string {
  return `${failed} ${failed === 1 ? 'falha' : 'falhas'}`;
}

/** "nos últimos 30 dias" — o número vem da resposta, nunca de um literal na tela. */
export function formatWindowSuffix(windowDays: number): string {
  return `nos últimos ${windowDays} dias`;
}

export interface ExecutionStatsLines {
  /** Linha 1 do card: "Última execução há 3 minutos · ✓ Concluída" | "Nunca executou". */
  lastExecution: string;
  /** Coluna "Última execução": o mesmo, sem o prefixo. */
  lastExecutionShort: string;
  /** "128 execuções" — `null` quando não rodou nada na janela. */
  counts: string | null;
  /** "3 falhas" — `null` quando não rodou nada na janela. */
  failures: string | null;
  /** Só então o trecho de falhas vai em `var(--theme-error-fg)`. */
  hasFailures: boolean;
  /** "nos últimos 30 dias". */
  windowSuffix: string;
  /** "nenhuma nos últimos 30 dias" — só quando já rodou um dia, mas não na janela. */
  noneInWindow: string | null;
}

/**
 * Rótulo da ÚLTIMA execução: o balde manda quando vem e tem rótulo próprio;
 * nos demais casos vale o status cru (é ele que distingue "Na fila" de
 * "Aguardando"). A leitura de um mapa devolve `undefined` em tempo de execução
 * para uma chave fora do enum — servidor mais novo que a tela, ou dado antigo —,
 * e é por isso que o `?? null` existe: sem ele a linha saía "há 3 minutos · "
 * com o separador pendurado.
 */
function resolveLastExecutionLabel(stats: WorkflowExecutionStats): string | null {
  const { lastExecutionBucket, lastExecutionStatus } = stats;

  if (lastExecutionBucket !== undefined) {
    const bucketLabel = LAST_EXECUTION_BUCKET_LABELS[lastExecutionBucket] ?? null;
    if (bucketLabel !== null) return bucketLabel;
  }

  if (lastExecutionStatus === undefined) return null;
  return LAST_EXECUTION_STATUS_LABELS[lastExecutionStatus] ?? null;
}

/**
 * Monta as linhas da listagem a partir dos contadores do servidor.
 *
 * `now` existe para o teste ser determinístico. Sem ele o padrão é agora —
 * exatamente o que `formatDistanceToNow` faria (ela é `formatDistance` com
 * `Date.now()` como base; a saída é a mesma, mas não dá para injetar o "agora").
 */
export function describeExecutionStats(
  stats: WorkflowExecutionStats,
  now: Date = new Date(),
): ExecutionStatsLines {
  const windowSuffix = formatWindowSuffix(stats.windowDays);
  const ranInWindow = stats.total > 0;
  const counts = ranInWindow ? formatExecutionCount(stats.total) : null;
  const failures = ranInWindow ? formatFailureCount(stats.failed) : null;
  const hasFailures = stats.failed > 0;

  // Ausente, nulo (JSON) ou vazio = não há última execução a mostrar.
  if (!stats.lastExecutionAt) {
    return {
      lastExecution: NEVER_EXECUTED_LABEL,
      lastExecutionShort: NEVER_EXECUTED_LABEL,
      counts,
      failures,
      hasFailures,
      windowSuffix,
      noneInWindow: null,
    };
  }

  // Data ilegível não pode derrubar a listagem: `formatDistance` LANÇA RangeError
  // com data inválida, e isto roda dentro do render de cada card. Sem data legível
  // a linha sai só com o status (o servidor manda ISO ou omite — é defesa, não caminho normal).
  const lastExecutionDate = new Date(stats.lastExecutionAt);
  const relative = Number.isNaN(lastExecutionDate.getTime())
    ? null
    : formatDistance(lastExecutionDate, now, { addSuffix: true, locale: ptBR });
  const statusLabel = resolveLastExecutionLabel(stats);
  // `typeof part === 'string'` e não `part !== null`: um rótulo ausente em tempo
  // de execução chega como `undefined`, que passaria pelo teste contra `null`.
  const lastExecutionParts = [relative, statusLabel].filter(
    (part): part is string => typeof part === 'string',
  );
  const lastExecutionShort = lastExecutionParts.length > 0 ? lastExecutionParts.join(' · ') : '—';

  return {
    lastExecution: `Última execução ${lastExecutionShort}`,
    lastExecutionShort,
    counts,
    failures,
    hasFailures,
    windowSuffix,
    // Rodou um dia, mas nada na janela: a tela diz as duas coisas numa linha só.
    noneInWindow: ranInWindow ? null : `nenhuma ${windowSuffix}`,
  };
}
